import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import analyticsService, { 
  trackPageView, 
  trackInteraction, 
  trackError,
  trackFormSubmission,
  trackRecommendation,
  trackFreeEventSignup,
  trackTicketPurchase,
  trackOrganizationCreation,
  trackEventCreation,
  trackEventShare,
  trackHomeButtonClick,
  trackEventRegistration
} from '../services/analytics.service';
import { useAppData } from './DataContext';

// Create context
export const AnalyticsContext = createContext(null);

/**
 * Analytics Provider Component
 * Initializes the analytics service and provides tracking methods to children
 */
export const AnalyticsProvider = ({ children }) => {
  const location = useLocation();
  const { userInfo } = useAppData();

  // Initialize analytics on first render
  useEffect(() => {
    analyticsService.init({
      enableMixpanel: process.env.NODE_ENV !== 'test',
      enableInternal: true
    });
  }, []);

  // Set user when userInfo changes
  useEffect(() => {
    if (userInfo && userInfo._id) {
      const userProperties = {
        email: userInfo.email,
        name: userInfo.name,
        createdAt: userInfo.createdAt,
        roles: userInfo.roles || []
      };
      
      analyticsService.setUser(userInfo._id, userProperties);
    } else {
      analyticsService.clearUser();
    }
  }, [userInfo]);

  // Track page views
  useEffect(() => {
    trackPageView({
      path: location.pathname,
      search: location.search
    });
  }, [location.pathname, location.search]);

  // Create context value
  const value = useMemo(() => ({
    // Core tracking functions
    trackEvent: analyticsService.track.bind(analyticsService),
    trackPageView,
    trackInteraction,
    trackError,
    trackFormSubmission,
    trackRecommendation,
    
    // New business-specific tracking functions
    trackFreeEventSignup,
    trackTicketPurchase,
    trackOrganizationCreation,
    trackEventCreation,
    trackEventShare,
    trackHomeButtonClick,
    trackEventRegistration,

    // Component-specific helpers
    trackButtonClick: (buttonName, properties = {}) => {
      trackInteraction('click', 'button', buttonName, properties);
    },
    
    trackLinkClick: (linkText, linkUrl, properties = {}) => {
      trackInteraction('click', 'link', linkText, { linkUrl, ...properties });
    },
    
    trackModalOpen: (modalName, properties = {}) => {
      trackInteraction('open', 'modal', modalName, properties);
    },
    
    trackModalClose: (modalName, properties = {}) => {
      trackInteraction('close', 'modal', modalName, properties);
    },
    
    trackSearch: (searchTerm, results, properties = {}) => {
      trackInteraction('search', 'searchbar', searchTerm, { 
        resultCount: results?.length,
        ...properties 
      });
    },
    
    trackFilter: (filterName, filterValue, properties = {}) => {
      trackInteraction('filter', 'filter', filterName, { 
        filterValue,
        ...properties 
      });
    },
    
    // Event-specific helpers
    trackEventView: (eventId, eventName, properties = {}) => {
      analyticsService.track('Event View', { 
        eventId,
        eventName,
        ...properties 
      });
      
      // Also track for recommendation engine
      trackRecommendation('view', eventId, 'event', properties);
    },
    
    // Updated event registration tracker
    trackEventRegistration: (eventId, eventName, ticketType, price = 0, properties = {}) => {
      const registrationType = price > 0 ? 'paid' : 'free';
      const ticketInfo = {
        ticketType,
        price
      };
      
      if (registrationType === 'free') {
        trackFreeEventSignup(eventId, eventName, ticketInfo, properties);
      } else {
        trackTicketPurchase(eventId, eventName, ticketInfo, price, 'USD', properties);
      }
      
      // Also track general registration for recommendation engine
      trackEventRegistration(eventId, eventName, registrationType, ticketInfo, properties);
    },
    
    // Home page specific tracking
    trackHomeButtonClick: (buttonId, buttonText, section, properties = {}) => {
      trackHomeButtonClick(buttonId, buttonText, section, properties);
    },
    
    // Event sharing tracker
    trackEventShare: (eventId, eventName, shareMethod, properties = {}) => {
      trackEventShare(eventId, eventName, shareMethod, properties);
    },
    
    // Organization-specific helpers
    trackOrgView: (orgId, orgName, properties = {}) => {
      analyticsService.track('Organization View', { 
        orgId,
        orgName,
        ...properties 
      });
      
      // Also track for recommendation engine
      trackRecommendation('view', orgId, 'organization', properties);
    }
  }), []);

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

/**
 * Custom hook to use analytics in components
 * @returns {Object} Analytics tracking functions
 */
export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};

// HOC to wrap components that need analytics
export const withAnalytics = (Component) => {
  return (props) => {
    const analytics = useAnalytics();
    return <Component {...props} analytics={analytics} />;
  };
}; 